/**
 * Match Lab's sign-off (Ben, 2026-10-22): "Set as human reviewed" and
 * "Unset human review" as deliberate buttons, each with a REQUIRED comment —
 * what was checked, or why it is being reopened. The same per-game record as
 * Match Administration's "Human reviewed" box (game_review_events): a change
 * is one appended row there, and its comment is a staff note posted right
 * after, so the Notes & review timeline shows the two together.
 *
 * Not one write: game_review_events has no comment column (adding one is a
 * migration, not taken yet). The review change goes first — a note that
 * says "set as reviewed" for a change that failed would be false — and a
 * comment that then fails to save is reported, not silently lost.
 */
import { useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'

type ReviewState = {
  reviewed: boolean
  reviewed_by: string | null
  reviewed_at: string | null
}

// The notes endpoint's limit (MAX_NOTE_CHARS), less the prefix.
const MAX_CHARS = 3900

const when = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString(undefined,
    { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''
const msg = (e: unknown) => (e instanceof ApiError ? e.message : String(e))

export function ReviewSignOff({ gameId, onChanged }: {
  gameId: number
  /** After a change and its comment are saved: the timeline reloads. */
  onChanged: () => void
}) {
  const [state, setState] = useState<ReviewState | null>(null)
  const [readError, setReadError] = useState<string | null>(null)
  /** The change the open form makes, fixed when it opens: the state
   *  flips the moment the change saves, before its comment does. */
  const [pending, setPending] = useState<boolean | null>(null)
  const [comment, setComment] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    api<ReviewState>(`/api/v1/opsqa/games/${gameId}/review`)
      .then((d) => {
        if (!live) return
        setState(typeof d?.reviewed === 'boolean' ? d : null)
        setReadError(typeof d?.reviewed === 'boolean' ? null : 'unexpected answer')
      })
      // "not reviewed" and "could not ask" must not look the same
      .catch((e: unknown) => { if (live) setReadError(msg(e)) })
    return () => { live = false }
  }, [gameId])

  if (readError) {
    return <p className="error">Human review status could not be read — {readError}</p>
  }
  if (!state) return null

  const open = pending != null
  const target = pending ?? !state.reviewed
  const text = comment.trim()
  const submit = async () => {
    if (!text || text.length > MAX_CHARS) return
    setSaving(true)
    setError(null)
    let changed: ReviewState
    try {
      changed = await api<ReviewState>(`/api/v1/opsqa/games/${gameId}/review`,
        { method: 'POST', body: JSON.stringify({ reviewed: target }) })
    } catch (e: unknown) {
      setError(`Not changed — ${msg(e)}`)
      setSaving(false)
      return
    }
    setState(changed)
    try {
      await api(`/api/v1/opsqa/games/${gameId}/notes`, {
        method: 'POST',
        body: JSON.stringify({
          body: `${target ? 'Set as human reviewed' : 'Unset human review'}: ${text}`,
        }),
      })
      setComment('')
      setPending(null)
    } catch (e: unknown) {
      // The change stands; keep the comment in the box to add as a note.
      setError(`${target ? 'Marked reviewed' : 'Review unset'}, but the comment `
        + `was not saved — ${msg(e)}. Add it as a note below.`)
    } finally {
      setSaving(false)
      onChanged()
    }
  }

  return (
    <section className="ls-notes" data-testid="review-signoff">
      <div className="ls-notes__head" style={{ alignItems: 'center' }}>
        <span className="t-label">Human review</span>
        <span className={state.reviewed ? '' : 'muted'}
              style={state.reviewed ? { color: 'var(--color-success, #22c55e)' } : undefined}>
          {state.reviewed
            ? `Reviewed${state.reviewed_by ? ` by ${state.reviewed_by}` : ''}`
              + `${state.reviewed_at ? ` · ${when(state.reviewed_at)}` : ''}`
            : 'Not reviewed'}
        </span>
        {!open && (
          <button type="button" className="adm-btn" style={{ marginLeft: 'auto' }}
                  onClick={() => { setPending(!state.reviewed); setError(null) }}>
            {target ? 'Set as human reviewed' : 'Unset human review'}
          </button>
        )}
      </div>
      {open && (
        <div className="ls-notes__add">
          <textarea className="adm-input" rows={2} value={comment} autoFocus
                    aria-label={target ? 'Review comment' : 'Reason for unsetting'}
                    placeholder={target
                      ? 'What did you check? Required.'
                      : 'Why is the review being unset? Required.'}
                    onChange={(e) => setComment(e.target.value)} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <button type="button" className="adm-btn"
                    disabled={saving || !text || text.length > MAX_CHARS}
                    onClick={() => void submit()}>
              {target ? 'Set as reviewed' : 'Unset review'}
            </button>
            <button type="button" className="adm-btn" disabled={saving}
                    onClick={() => { setPending(null); setComment(''); setError(null) }}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {text.length > MAX_CHARS && (
        <p className="error">A comment is at most {MAX_CHARS} characters.</p>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  )
}
